/**
 * Shared formatting utilities for the data-visualization layer.
 *
 * One canonical convention for currency, numbers, percentages, and dates so
 * every widget/chart/axis renders values the same way. Prior to this module
 * each dashboard component defined its own `formatCurrency`/`formatDate`, which
 * produced two currency conventions on one screen ($1,245,000 vs $1.2M),
 * per-row decimal drift, and a UTC-vs-local off-by-one date bug.
 *
 * Currency:
 *   - `formatCurrency`         full precision with thousands separators, e.g. "$1,245,000" / "-$500".
 *                              Use in detailed data tables (transactions, AR, cost/reorder).
 *   - `formatCurrencyCompact`  abbreviated, e.g. "$1.2M" / "$12.5K" / "$950" / "-$1.2M".
 *                              Use for KPI-style tiles and chart axes.
 *   - `setDisplayCurrency` / `getDisplayCurrency`
 *                              module-level tenant display currency (default "USD"). Both
 *                              `formatCurrency` and `formatCurrencyCompact` fall back to it when
 *                              no explicit `currency` is given. A Stripe-priced figure (billing
 *                              plans, checkout) is always USD and must pass `{ currency: 'USD' }`
 *                              explicitly, because it is priced in USD regardless of what a
 *                              tenant has chosen to see everywhere else.
 * Percent:
 *   - `formatPercent`          unsigned, finite-guarded, e.g. "12.3%".
 *   - `formatSignedPercent`    signed for deltas, e.g. "+12.3%" / "-2.3%".
 * Number:
 *   - `formatNumber`           grouped integer/decimal, finite-guarded.
 * Dates (backend emits date-only "YYYY-MM-DD"):
 *   - `parseLocalDate`         parses "YYYY-MM-DD" as LOCAL midnight (not UTC) to avoid the
 *                              off-by-one where US users saw the prior calendar day.
 *   - `formatDate`             parseLocalDate + toLocaleDateString.
 *   - `formatRelativeDay`      "Today" / "Yesterday" / "N days ago" / short date, by LOCAL calendar day.
 */

const MISSING = '—'; // em dash — shown when a value is not a finite number

const toNum = (value: number | string | null | undefined): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value);
  return NaN;
};

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

export interface CurrencyOptions {
  /** Show cents (2 fraction digits). Default false → whole dollars. */
  cents?: boolean;
  /** String returned for non-finite input. Default "—". */
  fallback?: string;
  /** ISO currency code. Default is the display currency (see setDisplayCurrency). */
  currency?: string;
}

/**
 * Module-level tenant display currency. Defaults to USD, which is also the
 * fast literal-"$" path both formatters take, so nothing user-visible changes
 * until something calls setDisplayCurrency.
 */
let displayCurrency = 'USD';

/** A three-letter uppercase ISO 4217 code. Intl throws RangeError on anything else. */
const VALID_CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * Sets the module-level display currency. Silently falls back to USD for
 * anything that is not a bare three-letter code (wrong case, a symbol, an
 * empty string), because passing a malformed code straight to Intl throws
 * RangeError rather than returning a sane default.
 */
export const setDisplayCurrency = (code: string): void => {
  displayCurrency = VALID_CURRENCY_CODE.test(code) ? code : 'USD';
};

/** The current module-level display currency. Defaults to "USD". */
export const getDisplayCurrency = (): string => displayCurrency;

/** Full-precision currency: "$1,245,000", "-$500". Non-finite → fallback ("—"). */
export const formatCurrency = (
  value: number | string | null | undefined,
  opts: CurrencyOptions = {}
): string => {
  const n = toNum(value);
  if (!Number.isFinite(n)) return opts.fallback ?? MISSING;
  const digits = opts.cents ? 2 : 0;
  // An empty string is treated as absent, not as a currency code: a caller
  // passing through a backend field that can come back "" (CollectionsWidget
  // passes invoice.currency) would otherwise reach Intl and throw RangeError.
  const currency = opts.currency || getDisplayCurrency();
  if (currency.toUpperCase() !== 'USD') {
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${sign}$${abs}`;
};

/** The Intl currency prefix for a code, e.g. "$" for USD, "€" for EUR, "CA$" for CAD. */
const currencyPrefix = (currency: string): string => {
  const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? currency;
};

/**
 * Abbreviated currency for KPI tiles & chart axes: "$1.2M", "$12.5K", "$950",
 * "-$1.2M". `currency` overrides the display currency; a non-USD code keeps
 * the same K/M/B thresholds and takes its prefix from Intl.
 */
export const formatCurrencyCompact = (
  value: number | string | null | undefined,
  fallback: string = MISSING,
  currency?: string
): string => {
  const n = toNum(value);
  if (!Number.isFinite(n)) return fallback;
  // Same empty-string-is-absent treatment as formatCurrency above.
  const cur = currency || getDisplayCurrency();
  const prefix = cur.toUpperCase() === 'USD' ? '$' : currencyPrefix(cur);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  // Thresholds are 999_950 (not 1e6) etc. so a value that rounds UP to the next
  // unit at 1 decimal promotes cleanly — e.g. 999,950 → "$1.0M", not "$1000.0K".
  if (abs >= 999_950_000) return `${sign}${prefix}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 999_950) return `${sign}${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${prefix}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${prefix}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

// ---------------------------------------------------------------------------
// Percent
// ---------------------------------------------------------------------------

/** Unsigned percent: "12.3%". Non-finite → "N/A". */
export const formatPercent = (
  value: number | string | null | undefined,
  digits: number = 1
): string => {
  const n = toNum(value);
  if (!Number.isFinite(n)) return 'N/A';
  return `${n.toFixed(digits)}%`;
};

/** Signed percent for deltas: "+12.3%", "-2.3%", "0%". Non-finite → "N/A". */
export const formatSignedPercent = (
  value: number | string | null | undefined,
  digits: number = 1
): string => {
  const n = toNum(value);
  if (!Number.isFinite(n)) return 'N/A';
  // Round first so values that round to zero (incl. tiny negatives) read "0%",
  // and the sign is taken from the rounded value — never a stray "-0.0%".
  const rounded = Number(n.toFixed(digits));
  if (rounded === 0) return '0%';
  const sign = rounded > 0 ? '+' : '-';
  return `${sign}${Math.abs(rounded).toFixed(digits)}%`;
};

// ---------------------------------------------------------------------------
// Number
// ---------------------------------------------------------------------------

/** Grouped number: "1,234,567". Non-finite → fallback ("—"). */
export const formatNumber = (
  value: number | string | null | undefined,
  fallback: string = MISSING
): string => {
  const n = toNum(value);
  if (!Number.isFinite(n)) return fallback;
  return n.toLocaleString('en-US');
};

// ---------------------------------------------------------------------------
// Dates — backend emits date-only "YYYY-MM-DD"; parse as LOCAL to avoid off-by-one
// ---------------------------------------------------------------------------

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// A date-TIME with no timezone designator on the end. The backend stores UTC in
// naive columns and serialises them exactly like this, so the convention lives
// in the code and never reaches the wire. A browser parses this form as LOCAL,
// which adds the reader's offset to a moment that was already UTC. That is
// DYN-53: every assistant reply appeared to arrive four hours after the
// question it answered, and the gap moved with the season because it tracked
// the offset rather than being constant.
const NAIVE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Parse a backend date string as a Date.
 *
 * Three rules, and the difference between the first two is the point:
 *
 * - "YYYY-MM-DD" is a calendar DATE, not an instant, and is constructed at
 *   local midnight. Reading it as UTC midnight shows the previous day to
 *   everyone west of Greenwich.
 * - "YYYY-MM-DDTHH:MM:SS" with no zone is an INSTANT the backend means in UTC,
 *   so the marker it omitted is supplied here. Passing it to the native parser
 *   reads it as local time and is wrong by the reader's offset.
 * - Anything already carrying a Z or an offset is passed through untouched, so
 *   a value that says what it means is never shifted twice.
 *
 * Returns an Invalid Date for empty/garbage input — callers guard via formatDate.
 */
export const parseLocalDate = (value: string | null | undefined): Date => {
  if (!value) return new Date(NaN);
  const m = DATE_ONLY.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (NAIVE_TIMESTAMP.test(value)) return new Date(`${value.replace(' ', 'T')}Z`);
  return new Date(value);
};

/**
 * A server timestamp as a time of day in the reader's own zone ("3:48 AM").
 * Use this rather than `new Date(x).toLocaleTimeString()`, which is the shape
 * DYN-53 was: it reads the backend's naive UTC as local.
 */
export const formatServerTime = (
  value: string | null | undefined,
  fallback = '—'
): string => {
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * A server timestamp as a date and time in the reader's own zone. Same reason,
 * and the same replacement for `new Date(x).toLocaleString()`.
 */
export const formatServerDateTime = (
  value: string | null | undefined,
  fallback = '—'
): string => {
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString();
};

const DEFAULT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/** Format a date-only/ISO string for display. Invalid input → fallback ("—"). */
export const formatDate = (
  value: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTS,
  fallback: string = MISSING
): string => {
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('en-US', opts);
};

/** Local-midnight timestamp for a Date (strips the time component). */
const localMidnight = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Relative day label by LOCAL calendar day: "Today", "Yesterday", "N days ago"
 * (up to 6), otherwise a short date. Avoids the raw-millisecond diff bug where
 * the label flipped across local midnight.
 */
export const formatRelativeDay = (
  value: string | null | undefined,
  fallback: string = MISSING
): string => {
  const d = parseLocalDate(value);
  if (Number.isNaN(d.getTime())) return fallback;
  const days = Math.round((localMidnight(new Date()) - localMidnight(d)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return `${days} days ago`;
  return formatDate(value, { month: 'short', day: 'numeric' }, fallback);
};
